import { useState, useMemo, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { Search, FileText } from "lucide-react";
import ProductCard from "@/components/ProductCard";
import PageHeader, { usePageTitle } from "@/components/PageHeader";
import { PRODUCTS, CATEGORIES } from "@/data/products";
import { useQuote } from "@/context/QuoteContext";

const ALL = "Tutti i prodotti";
const slug = (s) => s.toLowerCase().replace(/[^a-z]+/g, "-");

export default function Shop() {
  usePageTitle("/shop");
  const [searchParams] = useSearchParams();
  const [category, setCategory] = useState(searchParams.get("cat") || ALL);
  const [query, setQuery] = useState("");
  const { count, openDialog } = useQuote();

  useEffect(() => {
    const c = searchParams.get("cat");
    if (c) setCategory(c);
  }, [searchParams]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return PRODUCTS.filter(
      (p) =>
        (category === ALL || p.category === category) &&
        (!q || [p.name, p.code, p.short_description].some((v) => v.toLowerCase().includes(q)))
    );
  }, [category, query]);

  const countFor = (cat) => (cat === ALL ? PRODUCTS.length : PRODUCTS.filter((p) => p.category === cat).length);

  return (
    <main data-testid="shop-page" className="min-h-screen">
      <PageHeader
        testId="shop-header"
        eyebrow="Shop / Catalogo prodotti"
        title="Shop"
        lead="Seleziona uno o più articoli e richiedi un preventivo personalizzato. Nessun pagamento online o acquisto diretto: ti ricontattiamo noi."
      />
      <section className="mx-auto max-w-[1600px] px-6 py-16 md:px-12 md:py-24">
        <div className="flex flex-col gap-6 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex flex-wrap gap-2">
            {[ALL, ...CATEGORIES].map((cat) => (
              <button
                key={cat}
                data-testid={`shop-filter-${slug(cat)}`}
                onClick={() => setCategory(cat)}
                className={`rounded-full border px-4 py-2 text-xs font-semibold uppercase tracking-[0.12em] transition-colors ${
                  category === cat ? "border-brand bg-brand text-white" : "border-white/15 text-white/60 hover:border-white/40 hover:text-white"
                }`}
              >
                {cat}
                <span className="ml-2 opacity-60">{countFor(cat)}</span>
              </button>
            ))}
          </div>
          <div className="relative w-full lg:max-w-md">
            <Search className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-white/40" />
            <input
              data-testid="shop-search-input"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Cerca per nome, codice o descrizione..."
              className="w-full rounded-md border border-white/15 bg-surface py-3 pl-11 pr-4 text-sm text-white placeholder:text-white/35 outline-none transition-colors focus:border-brand"
            />
          </div>
        </div>

        {filtered.length === 0 ? (
          <div data-testid="shop-no-results" className="py-24 text-center text-white/50">
            Nessun prodotto trovato per la ricerca corrente.
          </div>
        ) : (
          <div data-testid="shop-products-grid" className="mt-12 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {filtered.map((p) => (
              <ProductCard key={p.id} product={p} zoomable />
            ))}
          </div>
        )}
      </section>

      {count > 0 && (
        <div data-testid="floating-quote-bar" className="fixed inset-x-0 bottom-5 z-40 flex justify-center px-5 pr-24">
          <div className="flex items-center gap-4 rounded-full border border-white/15 bg-surface/95 py-2 pl-5 pr-2 shadow-2xl backdrop-blur">
            <span className="text-sm text-white">
              {count} {count === 1 ? "prodotto" : "prodotti"} nel preventivo
            </span>
            <button
              data-testid="floating-view-quote-button"
              onClick={openDialog}
              className="inline-flex items-center gap-2 rounded-full bg-brand px-5 py-2.5 text-xs font-semibold uppercase tracking-[0.12em] text-white hover:bg-brand-hover"
            >
              <FileText className="h-4 w-4" /> Vedi preventivo
            </button>
          </div>
        </div>
      )}
    </main>
  );
}
